import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/features/users/hooks';
import { useGetOfferQuery } from '@/features/offers/api';

type Mode = 'buyer' | 'seller';

export function useValidateOfferAccess(offerId: string, mode: Mode) {
  const navigate = useNavigate();

  const user = useCurrentUser();

  const { data: offer } = useGetOfferQuery({ id: offerId });

  const accessStatus = useMemo(() => {
    if (!user?.company || !offer) return 'loading';

    const userCompany = user.company.id ?? user.company;
    const buyerCompany = offer.company?.id ?? offer.company;
    const sellerCompany = offer.opportunity?.company?.id ?? offer.opportunity?.company;

    const isBuyer = userCompany === buyerCompany;
    const isSeller = userCompany === sellerCompany;

    // No access to the offer
    if (!isBuyer && !isSeller) return 'no_access';

    // Mode seller but user is buyer
    if (mode === 'seller' && !isSeller) return 'wrong_mode_buyer';

    // Mode buyer but user is seller
    if (mode === 'buyer' && !isBuyer) return 'wrong_mode_seller';

    return 'ok';
  }, [user, offer, mode]);

  useMemo(() => {
    if (accessStatus === 'no_access') {
      navigate('/', { replace: true });
    }

    if (accessStatus === 'wrong_mode_buyer') {
      navigate(`/buying/offers/${offerId}`, { replace: true });
    }

    if (accessStatus === 'wrong_mode_seller') {
      navigate(`/selling/offers/${offerId}`, { replace: true });
    }
  }, [accessStatus, offerId, navigate]);

  return {
    isLoading: accessStatus === 'loading',
    hasAccess: accessStatus === 'ok',
  };
}
